// datatru/static/js/components/sidebar.js
// Collapsible sidebar component with expanded/collapsed states,
// tooltip support, mobile overlay, and user avatar dropdown.

import { withThemeAware } from '../theme-toggle.js';

const STORAGE_KEY = 'sidebar-expanded';
const COLLAPSE_BREAKPOINT = 1024;

export function collapsibleSidebar() {
  return withThemeAware({
    expanded: true,
    mobileOpen: false,
    avatarOpen: false,
    avatarAnchor: null,
    tooltipText: '',
    tooltipVisible: false,
    tooltipY: 0,
    _tooltipTimer: null,
    _resizeHandler: null,

    init() {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored !== null) {
        this.expanded = stored === 'true';
      }

      this._resizeHandler = () => {
        if (window.innerWidth < COLLAPSE_BREAKPOINT) {
          this.expanded = false;
        }
        // Close avatar dropdown on resize so its captured anchor doesn't go stale
        this.closeAvatar();
      };
      window.addEventListener('resize', this._resizeHandler);

      // Close avatar dropdown whenever the sidebar collapses or expands —
      // its captured anchor would otherwise point at the wrong place.
      this.$watch('expanded', () => this.closeAvatar());
    },

    destroy() {
      if (this._resizeHandler) {
        window.removeEventListener('resize', this._resizeHandler);
        this._resizeHandler = null;
      }
    },

    toggle() {
      this.expanded = !this.expanded;
      localStorage.setItem(STORAGE_KEY, String(this.expanded));
    },

    openMobile() {
      this.mobileOpen = true;
    },

    closeMobile() {
      this.mobileOpen = false;
    },

    toggleAvatar(event) {
      this.avatarOpen = !this.avatarOpen;
      if (this.avatarOpen) {
        if (event && event.currentTarget) {
          const r = event.currentTarget.getBoundingClientRect();
          this.avatarAnchor = {
            top: r.top,
            bottom: r.bottom,
            left: r.left,
            right: r.right,
            width: r.width,
          };
        }
        this.hideTooltip();
      }
    },

    closeAvatar() {
      this.avatarOpen = false;
    },

    showTooltip(text, event) {
      if (this.expanded) return;
      if (this.avatarOpen) return;
      const target = event.currentTarget;
      const rect = target.getBoundingClientRect();
      this.tooltipText = text;
      this.tooltipY = rect.top + rect.height / 2;
      clearTimeout(this._tooltipTimer);
      this._tooltipTimer = setTimeout(() => {
        this.tooltipVisible = true;
      }, 200);
    },

    hideTooltip() {
      clearTimeout(this._tooltipTimer);
      this.tooltipVisible = false;
    },

    // Escape handler (CSP-safe: no compound expressions in templates)
    handleEscape() {
      this.closeMobile();
      this.closeAvatar();
    },

    // CSP-safe computed classes (ternaries not allowed in Alpine CSP templates)
    get sidebarWidth() {
      return this.expanded ? 'w-64' : 'w-16';
    },
    get sidebarPadding() {
      return this.expanded ? 'px-6' : 'px-2';
    },
    get logoJustify() {
      return this.expanded ? 'justify-between' : 'justify-center';
    },
    get contentOffset() {
      return this.expanded ? 'lg:pl-64' : 'lg:pl-16';
    },
    get sidebarInsetStyle() {
      return '--sidebar-inset: ' + (this.expanded ? '16rem' : '4rem');
    },
    get navItemCollapsed() {
      return this.expanded ? '' : 'justify-center';
    },
    get userPadding() {
      return this.expanded ? 'px-6' : 'px-2 justify-center';
    },
    get userMargin() {
      return this.expanded ? '-mx-6' : '-mx-2';
    },
    get dropdownFixedStyle() {
      const a = this.avatarAnchor;
      if (!a) return 'display: none;';
      if (this.expanded) {
        // Anchor above the trigger, full-width within the sidebar
        return (
          'left: ' +
          a.left +
          'px; bottom: ' +
          (window.innerHeight - a.top + 8) +
          'px; width: ' +
          a.width +
          'px;'
        );
      }
      // Collapsed: fly out to the right of the rail, bottom-aligned to the avatar
      return (
        'left: ' +
        (a.right + 8) +
        'px; bottom: ' +
        (window.innerHeight - a.bottom) +
        'px; width: 12rem;'
      );
    },
    get tooltipStyle() {
      const left = this.expanded ? 272 : 72;
      return (
        'left: ' +
        left +
        'px; top: ' +
        this.tooltipY +
        'px; transform: translateY(-50%);'
      );
    },
    get collapsed() {
      return !this.expanded;
    },
  });
}
